import { useMemo, useState } from "react";
import { STATUS, fmtDate, fmtShootTime } from "../lib/helpers";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { MapPin, Package, MagnifyingGlass, X, CaretDown, CaretUp } from "@phosphor-icons/react";

const dayKey = (iso) => (iso ? String(iso).slice(0, 10) : "");

export default function ShootsList({ properties, users, onOpen }) {
  const [status, setStatus] = useState("all");
  const [photographerId, setPhotographerId] = useState("all");
  const [agentId, setAgentId] = useState("all");
  const [day, setDay] = useState("all");
  const [query, setQuery] = useState("");
  const [sortDir, setSortDir] = useState("desc"); // by shoot_date

  const nameOf = (id) => {
    const u = users.find((x) => x.id === id);
    return u ? (u.name || u.email) : null;
  };

  const photographers = useMemo(() => users.filter((u) => u.role === "photographer"), [users]);
  const agents = useMemo(() => users.filter((u) => u.role === "agent"), [users]);

  const days = useMemo(() => {
    const set = new Set(properties.map((p) => dayKey(p.shoot_date)).filter(Boolean));
    return Array.from(set).sort();
  }, [properties]);

  const filtered = useMemo(() => {
    let rows = properties.filter((p) => {
      if (status !== "all" && p.status !== status) return false;
      if (photographerId !== "all") {
        if (photographerId === "unassigned" ? p.photographer_id : p.photographer_id !== photographerId) return false;
      }
      if (agentId !== "all" && p.agent_id !== agentId) return false;
      if (day !== "all" && dayKey(p.shoot_date) !== day) return false;
      if (query.trim()) {
        const q = query.toLowerCase();
        if (!(`${p.title || ""} ${p.address || ""}`.toLowerCase().includes(q))) return false;
      }
      return true;
    });
    rows = [...rows].sort((a, b) => {
      const av = a.shoot_date || "", bv = b.shoot_date || "";
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [properties, status, photographerId, agentId, day, query, sortDir]);

  const active = status !== "all" || photographerId !== "all" || agentId !== "all" || day !== "all" || query.trim();
  const clear = () => { setStatus("all"); setPhotographerId("all"); setAgentId("all"); setDay("all"); setQuery(""); };

  return (
    <div data-testid="shoots-list" className="lux-panel overflow-hidden">
      {/* Filter bar */}
      <div className="p-4 border-b border-[#F0ECE2] bg-[#FBFAF7] flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9A968E]" />
          <Input data-testid="list-search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search address or title…" className="pl-9 bg-white border-[#E9DDC6] h-9" />
        </div>

        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="list-filter-status" className="h-9 w-[150px] bg-white border-[#E9DDC6]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={photographerId} onValueChange={setPhotographerId}>
          <SelectTrigger data-testid="list-filter-photographer" className="h-9 w-[170px] bg-white border-[#E9DDC6]"><SelectValue placeholder="Photographer" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All photographers</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {photographers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={agentId} onValueChange={setAgentId}>
          <SelectTrigger data-testid="list-filter-agent" className="h-9 w-[160px] bg-white border-[#E9DDC6]"><SelectValue placeholder="Agent" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All agents</SelectItem>
            {agents.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={day} onValueChange={setDay}>
          <SelectTrigger data-testid="list-filter-day" className="h-9 w-[160px] bg-white border-[#E9DDC6]"><SelectValue placeholder="Day" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All days</SelectItem>
            {days.map((d) => <SelectItem key={d} value={d}>{fmtDate(d)}</SelectItem>)}
          </SelectContent>
        </Select>

        {active ? (
          <button data-testid="list-clear-filters" onClick={clear}
            className="h-9 px-3 flex items-center gap-1.5 text-sm text-[#4A0000] hover:bg-[#FAF9F6] rounded-md transition-colors">
            <X size={14} weight="bold" /> Clear
          </button>
        ) : null}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-[#9A968E] bg-white border-b border-[#F0ECE2]">
              <th className="p-4 font-semibold">Property</th>
              <th className="p-4 font-semibold">
                <button data-testid="list-sort-date" onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                  className="inline-flex items-center gap-1 hover:text-[#4A0000] transition-colors uppercase tracking-widest">
                  Date {sortDir === "asc" ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
                </button>
              </th>
              <th className="p-4 font-semibold">Status</th>
              <th className="p-4 font-semibold">Photographer</th>
              <th className="p-4 font-semibold">Agent</th>
              <th className="p-4 font-semibold">Package</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={6} className="p-10 text-center text-[#9A968E]" data-testid="list-empty">No shoots match these filters.</td></tr>
            ) : filtered.map((p) => {
              const st = STATUS[p.status] || STATUS.scheduled;
              return (
                <tr key={p.id} data-testid={`list-row-${p.id}`} onClick={() => onOpen(p.id)}
                  className="border-b border-[#FAF9F6] last:border-0 hover:bg-[#FBFAF7] cursor-pointer transition-colors">
                  <td className="p-4 max-w-[280px]">
                    <div className="font-display text-[#1A1A1A] truncate">{p.title}</div>
                    <div className="text-xs text-[#5C5A58] flex items-center gap-1 truncate"><MapPin size={12} weight="duotone" className="shrink-0" /><span className="truncate">{p.address}</span></div>
                  </td>
                  <td className="p-4 whitespace-nowrap text-[#5C5A58]">
                    <div>{fmtDate(p.shoot_date) || "—"}</div>
                    {fmtShootTime(p.shoot_date) && <div className="text-xs">{fmtShootTime(p.shoot_date)}</div>}
                  </td>
                  <td className="p-4"><span className={`pill ${st.cls}`}>{st.label}</span></td>
                  <td className="p-4 whitespace-nowrap text-[#5C5A58]">{nameOf(p.photographer_id) || <span className="text-[#B4472E]">Unassigned</span>}</td>
                  <td className="p-4 whitespace-nowrap text-[#5C5A58]">{nameOf(p.agent_id) || "—"}</td>
                  <td className="p-4 whitespace-nowrap">
                    {p.package_type ? (
                      <span className="inline-flex items-center gap-1.5">
                        <Badge className="bg-[#4A0000]/5 text-[#4A0000] border-[#4A0000]/20 flex items-center gap-1"><Package size={11} weight="fill" />{p.package_type}</Badge>
                        {p.drone && <Badge className="bg-[#B08D57]/15 text-[#8A6D1B] border-[#B08D57]/40">Drone</Badge>}
                      </span>
                    ) : <span className="text-[#9A968E]">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="px-4 py-3 border-t border-[#F0ECE2] bg-[#FBFAF7] text-xs text-[#5C5A58]" data-testid="list-count">
        Showing {filtered.length} of {properties.length} shoots
      </div>
    </div>
  );
}
